import greetingGlyph from "@/assets/ai-chat-actions/greeting-glyph.svg";
import { AUTH_PAGE_LAYOUT_CLASS } from "@/pages/auth-layout";
import { useSafeAreaStyles } from "@/hooks/utils/use-safe-area-styles";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonContent, IonPage } from "@ionic/react";
import { ReactNode } from "react";

/** Solid dark background used by the redesigned onboarding auth screens ("Onboarding bg" in Figma). */
export const AUTH_DARK_BG = "#130C2D";

/** Accent purple used for links on the dark auth screens. */
export const AUTH_LINK_CLASS = "text-[#9f56ff]";

/**
 * Overrides for the primary auth CTA: a white pill button with purple label and the subtle
 * inset shadow from the Figma "Button Pill Tall" component. Pair with `<FilledButton color="white" />`.
 */
export const AUTH_CTA_CLASS =
  "!text-[#620ad5] shadow-[inset_0px_-12px_6px_0px_rgba(98,10,213,0.06)]";

interface AuthScreenLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * Shared scaffold for the dark-themed auth screens (login, register, reset password).
 * Renders the dark background, the NutriCoach glyph, and a centered serif title + subtitle,
 * then the page-specific form as children.
 */
export function AuthScreenLayout({ title, subtitle, children }: AuthScreenLayoutProps) {
  const safeAreaStyles = useSafeAreaStyles();

  return (
    <IonPage style={{ ...safeAreaStyles }}>
      <IonContent fullscreen style={{ "--background": AUTH_DARK_BG } as React.CSSProperties}>
        <div
          className={cn(
            "mx-auto flex h-full w-full max-w-[450px] flex-col",
            AUTH_PAGE_LAYOUT_CLASS,
          )}
          style={{ backgroundColor: AUTH_DARK_BG }}
        >
          <div className="flex w-full flex-col items-center gap-8">
            <img src={greetingGlyph} alt="" aria-hidden className="h-[31px] w-[43px]" />

            <div className="flex w-full flex-col items-center gap-4 text-center">
              <h1
                className={cn(
                  textVariants.serif,
                  "text-[34px] leading-tight font-semibold text-white",
                )}
              >
                {title}
              </h1>
              {subtitle && (
                <p className={cn(textVariants.bodySingleLine, "text-[#d1cbe7]")}>{subtitle}</p>
              )}
            </div>
          </div>

          {children}
        </div>
      </IonContent>
    </IonPage>
  );
}
